import { useMemo, useState } from 'react';

import { useMutation } from '@apollo/client';
import {
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography,
} from '@mui/material';
import { Formik } from 'formik';
import { SuspendedReason } from 'types/graphql';

import { MetaTags, useQuery } from '@redwoodjs/web';

import { CustomInput } from 'src/components/CustomInput';
import Loader from 'src/components/Loader/Loader';
import { ConfirmationModal } from 'src/components/Modals';
import TableList from 'src/components/TableList/TableList';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import StatusChip from 'src/components/UserStatusChip/UserStatusChip';
import { CANCELLATION_REASONS_TABLE_HEADERS } from 'src/constants';
import { useAuthContext } from 'src/contexts/AuthProvider';
import {
    CREATE_SUSPENDED_REASON_MUTATION,
    DELETE_SUSPENDED_REASON_MUTATION,
} from 'src/graphql/mutations/suspendedReasons.mutation';
import { GET_SUSPENDED_REASONS } from 'src/graphql/queries/suspendedReasons.query';
import { PageContent } from 'src/layouts/MainLayout/PageContent';

import EditSuspendedReasonModalContent from './EditSuspendedReasonModalContent';
import './SuspendedReasonsPageStyles.css';

const SuspendedReasonsPage = () => {
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openSuspendedReasonModal, setOpenSuspendedModal] = useState(false);
    const [selectedReason, setSelectedReason] = useState<SuspendedReason>();
    const [id, setId] = useState<number | null>();
    const [status, setStatus] = useState('ACTIVE');
    const [description, setDescription] = useState('');
    const { user } = useAuthContext();

    const [viewingReason, setViewingReason] = useState<{
        description: string;
        status: string;
    }>({
        description: '',
        status: '',
    });

    const { data, loading, refetch } = useQuery(GET_SUSPENDED_REASONS, {
        fetchPolicy: 'cache-and-network',
        onError: () => {
            return showErrorToast('There was an error retrieving cancellation reasons');
        },
    });

    const [deleteSuspendedReason] = useMutation(DELETE_SUSPENDED_REASON_MUTATION, {
        onCompleted: () => {
            showSuccessToast('Suspend Reason Deleted Successfully');
            refetch();
        },
        onError: (error) => {
            showErrorToast(error.message ?? 'Error Deleting the Reason');
        },
    });

    const [createSuspendedReasonMutation] = useMutation(CREATE_SUSPENDED_REASON_MUTATION, {
        onCompleted: () => {
            handleCloseSuspendedReasonModal();
            showSuccessToast('Suspend Reason Created Successfully');
            refetch();
        },
        onError: () => {
            showErrorToast('Error Creating Cancellation Reason');
        },

        variables: {
            input: {
                status,
                description,
                createdBy: user.id,
                lastModifiedBy: user.id,
            },
        },
    });

    const tableData = useMemo(() => {
        if (data?.suspendedReasons?.length) {
            return data.suspendedReasons.map((reason) => {
                return {
                    ...reason,
                    dateCreated: reason.createdAt,
                    status: <StatusChip status={reason?.status} type="suspendedReasons" />,
                };
            });
        }
        return [];
    }, [data]);

    const handleOpenDeleteModal = (id, reason) => {
        setId(id);
        setViewingReason({
            description: reason.description,
            status: reason.status.props.status,
        });
        setOpenDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
    };

    // Function used to delete the specified cancellation reason
    const handleDeleteRecord = () => {
        // GraphQL mutation
        deleteSuspendedReason({ variables: { id } });
        handleCloseDeleteModal();
    };

    const handleCloseSuspendedReasonModal = () => {
        setOpenSuspendedModal(false);
    };
    const handleAddSuspendedReason = () => {
        // Open add reason modal
        setOpenSuspendedModal(true);
    };

    const onCloseEditSuspendedReasonModal = () => {
        setSelectedReason(null);
    };

    const onConfirmEditSuspendedReasonModal = async () => {
        await refetch();
        onCloseEditSuspendedReasonModal();
    };

    return (
        <PageContent heading="Suspend Reasons" subHeading="Add, view and remove suspend reasons">
            <MetaTags title="Suspend Reasons" description="Suspend Reasons Page" />

            <Grid container sx={{ gap: '24px' }}>
                <Grid item xs={12}>
                    {loading ? (
                        <Loader />
                    ) : (
                        <TableList
                            addRecordButtonText="Add Suspend Reason"
                            data={tableData}
                            headers={CANCELLATION_REASONS_TABLE_HEADERS}
                            title="Suspend Reasons List"
                            addRecordCallBack={handleAddSuspendedReason}
                            deleteRecordCallBack={handleOpenDeleteModal}
                            handleEditRecord={(reason) => {
                                const selectedReasonId: number = (reason as SuspendedReason).id;
                                setSelectedReason(
                                    data.suspendedReasons.find(({ id }) => {
                                        return id === selectedReasonId;
                                    })
                                );
                            }}
                            enableExport
                            filterOptions={{}}
                        />
                    )}

                    <ConfirmationModal
                        isOpen={openDeleteModal}
                        title="Delete Suspend Reason"
                        justifyButtonContent="end"
                        cancelBtnVariant="back-button"
                        dialogActionMarginTop={'14px'}
                        message={
                            <>
                                <Grid
                                    item
                                    xs={12}
                                    lg={12}
                                    width={800}
                                    container
                                    direction="row"
                                    alignItems="center"
                                    gap={4}
                                    marginBottom={3}
                                >
                                    <Grid item>
                                        <Typography
                                            sx={{
                                                color: '#000',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Are you sure you want to delete this Suspend Reason?
                                        </Typography>
                                    </Grid>
                                    <Grid item container direction={'row'} gap={3}>
                                        <Grid item>
                                            <Typography
                                                sx={{
                                                    color: '#000',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Status
                                            </Typography>
                                        </Grid>
                                        <Grid item>
                                            <Typography
                                                sx={{
                                                    color: '#000',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {viewingReason?.status}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid container direction={'column'} item xs={12} md={12} gap={3}>
                                    <Grid item>
                                        <Typography
                                            sx={{
                                                color: '#000',
                                                fontWeight: 600,
                                            }}
                                        >
                                            Suspend Reason
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography
                                            sx={{
                                                color: '#000',
                                                fontWeight: 400,
                                            }}
                                        >
                                            {viewingReason?.description}
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </>
                        }
                        onCancel={handleCloseDeleteModal}
                        onConfirm={() => {
                            handleDeleteRecord();
                            handleCloseDeleteModal();
                        }}
                    />
                    <ConfirmationModal
                        isOpen={openSuspendedReasonModal}
                        title="Add Suspend Reason"
                        justifyButtonContent="end"
                        dialogActionMarginTop={'14px'}
                        cancelBtnVariant="back-button"
                        message={
                            <Formik>
                                <>
                                    <Grid
                                        item
                                        xs={12}
                                        lg={12}
                                        width={800}
                                        container
                                        direction="row"
                                        alignItems="center"
                                        gap="16px"
                                        marginBottom={3}
                                    >
                                        <Grid item>
                                            <FormLabel
                                                id="suspended-reason-status"
                                                sx={{
                                                    color: '#000',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                Status
                                            </FormLabel>
                                        </Grid>
                                        <Grid item>
                                            <FormControl>
                                                <RadioGroup
                                                    aria-labelledby="radio-buttons"
                                                    defaultValue="ACTIVE"
                                                    name="status-group"
                                                    row
                                                    onChange={(event) => {
                                                        setStatus(event.target.value);
                                                    }}
                                                >
                                                    <FormControlLabel
                                                        value="ACTIVE"
                                                        control={
                                                            <Radio
                                                                size="small"
                                                                sx={{
                                                                    '&.Mui-checked': {
                                                                        '& .MuiSvgIcon-root': {
                                                                            borderRadius: '50%',
                                                                            border: '6px solid #F18F34',
                                                                            backgroundColor:
                                                                                'white',
                                                                            color: 'white',
                                                                        },
                                                                    },

                                                                    '& .MuiSvgIcon-root': {
                                                                        color: 'white',
                                                                        borderRadius: '50%',
                                                                        border: '1px solid #F18F34',
                                                                        backgroundColor: 'white',
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        label="ACTIVE"
                                                    />
                                                    <FormControlLabel
                                                        value="INACTIVE"
                                                        control={
                                                            <Radio
                                                                size="small"
                                                                sx={{
                                                                    '&.Mui-checked': {
                                                                        '& .MuiSvgIcon-root': {
                                                                            borderRadius: '50%',
                                                                            border: '6px solid #F18F34',
                                                                            backgroundColor:
                                                                                'white',
                                                                            color: 'white',
                                                                            marginLeft: '1rem',
                                                                        },
                                                                    },

                                                                    '& .MuiSvgIcon-root': {
                                                                        color: 'white',
                                                                        marginLeft: '1rem',
                                                                        borderRadius: '50%',
                                                                        border: '1px solid #F18F34',
                                                                        backgroundColor: 'white',
                                                                    },
                                                                }}
                                                            />
                                                        }
                                                        label="INACTIVE"
                                                    />
                                                </RadioGroup>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <CustomInput
                                            name="Suspend Reason"
                                            textFieldBorderRadius="4px"
                                            textFieldPadding="31.5px 14px"
                                            alignInput="top"
                                            label="Suspend Reason"
                                            size="large"
                                            maxLength={50}
                                            onChange={(e) => {
                                                return setDescription(e.target.value);
                                            }}
                                        />
                                    </Grid>
                                </>
                            </Formik>
                        }
                        onCancel={handleCloseSuspendedReasonModal}
                        onConfirm={() => {
                            createSuspendedReasonMutation();
                            handleCloseSuspendedReasonModal();
                        }}
                    />

                    <ConfirmationModal
                        isOpen={selectedReason?.description?.length > 0}
                        modalWidth="30%"
                        onCancel={onCloseEditSuspendedReasonModal}
                        title="Edit Suspend Reason"
                        hideCancelButton
                        message={
                            <EditSuspendedReasonModalContent
                                selectedReasonId={selectedReason?.id}
                                description={selectedReason?.description}
                                status={selectedReason?.status}
                                onCancel={onCloseEditSuspendedReasonModal}
                                onConfirm={onConfirmEditSuspendedReasonModal}
                            />
                        }
                    />
                </Grid>
            </Grid>
        </PageContent>
    );
};

export default SuspendedReasonsPage;
